/* =============================================================================
 * accessibility.js — runtime WCAG 2.1 AA hardening
 *
 * 1. inert-sync: any element marked aria-hidden="true" (e.g. off-screen
 *    Flickity carousel slides) is also made non-focusable, so keyboard focus
 *    never lands in hidden content. Fixes the "aria-hidden-focus" violation
 *    without editing the minified vendor bundle. (WCAG 4.1.2)
 *
 * 2. div-toggle keyboard support: elements that behave like buttons via a
 *    click handler but are not <button>/<a> (the cart toggle) are made
 *    operable with Enter/Space. (WCAG 2.1.1)
 *
 * 3. aria-expanded sync for the header burger / search toggles. (WCAG 4.1.2)
 * ========================================================================== */
(function () {
  'use strict';

  var supportsInert = ('inert' in HTMLElement.prototype);
  var FOCUSABLE = 'a[href], area[href], button, input, select, textarea, ' +
    'iframe, audio[controls], video[controls], [contenteditable], [tabindex]';

  /* ---- 1. inert sync ----------------------------------------------------- */
  function hide(el) {
    if (supportsInert) {
      el.inert = true;
      return;
    }
    var nodes = el.querySelectorAll(FOCUSABLE);
    for (var i = 0; i < nodes.length; i++) {
      var n = nodes[i];
      if (!n.hasAttribute('data-a11y-tabindex')) {
        n.setAttribute('data-a11y-tabindex',
          n.hasAttribute('tabindex') ? n.getAttribute('tabindex') : 'none');
      }
      n.setAttribute('tabindex', '-1');
    }
  }

  function show(el) {
    if (supportsInert) {
      el.inert = false;
      return;
    }
    var nodes = el.querySelectorAll('[data-a11y-tabindex]');
    for (var i = 0; i < nodes.length; i++) {
      var n = nodes[i];
      var prev = n.getAttribute('data-a11y-tabindex');
      if (prev === 'none') { n.removeAttribute('tabindex'); }
      else { n.setAttribute('tabindex', prev); }
      n.removeAttribute('data-a11y-tabindex');
    }
  }

  /* Product thumbnail-nav cells must NEVER be inert/aria-hidden. They are a
     visible click target containing only <span><img> (nothing focusable), so
     inert serves no a11y purpose here — but Flickity marks NON-selected cells
     inert even while they're fully visible, and our [inert]{pointer-events:none}
     then made them unclickable. Because the strip re-centers after each click,
     which cells are inert changes every time → the "sometimes a thumbnail does
     nothing" bug. Strip inert/aria-hidden from anything inside the dots strip. */
  function freeNavThumbnails() {
    var strips = document.querySelectorAll('.product-hero__images-dots');
    for (var s = 0; s < strips.length; s++) {
      var els = strips[s].querySelectorAll('[inert], [aria-hidden="true"]');
      for (var i = 0; i < els.length; i++) {
        var c = els[i];
        if (c.inert) { try { c.inert = false; } catch (e) {} }
        if (c.hasAttribute('inert')) { c.removeAttribute('inert'); }
        if (c.getAttribute('aria-hidden') === 'true') { c.removeAttribute('aria-hidden'); }
      }
    }
  }

  function sync(el) {
    if (!el || el.nodeType !== 1) { return; }
    if (el.closest && el.closest('.product-hero__images-dots')) {
      if (el.inert) { try { el.inert = false; } catch (e) {} }
      if (el.hasAttribute('inert')) { el.removeAttribute('inert'); }
      if (el.getAttribute('aria-hidden') === 'true') { el.removeAttribute('aria-hidden'); }
      return;
    }
    if (el.getAttribute('aria-hidden') === 'true') {
      /* Carousel libraries mark NON-SELECTED cells aria-hidden even when the
         cell is fully visible beside the selected one (variant-thumbnail
         strips, multi-card carousels). Combined with inert that made VISIBLE
         controls unclickable (found live: the second variant dot on product
         pages). The aria-hidden is the library's error there — remove it for
         cells substantially visible inside their carousel viewport; truly
         off-screen cells stay hidden+inert (the original aria-hidden-focus
         fix). The library re-applies on scroll; the observer re-syncs. */
      if (el.closest && el.closest('.flickity-slider')) {
        var vp = el.closest('.flickity-viewport');
        if (vp) {
          var r = el.getBoundingClientRect();
          var v = vp.getBoundingClientRect();
          var overlap = Math.min(r.right, v.right) - Math.max(r.left, v.left);
          if (r.width > 0 && overlap > r.width * 0.6) {
            el.removeAttribute('aria-hidden');
            show(el);
            return;
          }
        }
      }
      hide(el);
    }
    else { show(el); }
  }

  function syncAll(root) {
    var nodes = (root || document).querySelectorAll('[aria-hidden="true"]');
    for (var i = 0; i < nodes.length; i++) { sync(nodes[i]); }
  }

  /* ---- 2. div / span button keyboard operability ------------------------- */
  function activate(e) {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault();
      e.currentTarget.click();
    }
  }
  function upgradeFauxButtons() {
    // The cart toggle renders as a <div data-module="header-cart-icon">.
    var faux = document.querySelectorAll(
      '[data-module="header-cart-icon"]:not([data-a11y-key])'
    );
    for (var i = 0; i < faux.length; i++) {
      var el = faux[i];
      // Skip on the cart page where it is not a toggle.
      if (el.closest && el.closest('.template--cart')) { continue; }
      if (!el.hasAttribute('tabindex')) { el.setAttribute('tabindex', '0'); }
      if (!el.hasAttribute('role')) { el.setAttribute('role', 'button'); }
      el.addEventListener('keydown', activate);
      el.setAttribute('data-a11y-key', '1');
    }
  }

  /* ---- 2b. ensure every form control has an accessible name -------------- */
  /* The theme's Vue-rendered inputs (text-input / select-input components)
     drop the label/aria-label set in Liquid, leaving controls named only by a
     placeholder. This guarantees a real accessible name on every control,
     bypassing the framework. (WCAG 1.3.1, 3.3.2, 4.1.2) */
  function hasName(el) {
    if (el.getAttribute('aria-label') && el.getAttribute('aria-label').trim()) { return true; }
    if (el.getAttribute('aria-labelledby')) { return true; }
    if (el.getAttribute('title') && el.getAttribute('title').trim()) { return true; }
    if (el.id) {
      try { if (document.querySelector('label[for="' + (window.CSS && CSS.escape ? CSS.escape(el.id) : el.id) + '"]')) { return true; } }
      catch (e) { /* invalid selector — ignore */ }
    }
    if (el.closest && el.closest('label')) { return true; }
    return false;
  }
  function nameFromAttrs(el) {
    var ph = el.getAttribute('placeholder');
    if (ph && ph.trim()) { return ph.trim(); }
    var name = el.getAttribute('name') || '';
    name = name.replace(/.*\[/, '').replace(/\].*/, '').replace(/[_\-]+/g, ' ').trim();
    return name || null;
  }
  function nameFormControls() {
    var controls = document.querySelectorAll(
      'input:not([type=hidden]):not([type=submit]):not([type=button]):not([type=image]):not([type=reset]), select, textarea'
    );
    for (var i = 0; i < controls.length; i++) {
      var el = controls[i];
      if (hasName(el)) { continue; }
      var label = nameFromAttrs(el);
      if (label) { el.setAttribute('aria-label', label); }
    }
  }

  /* ---- 2b1b. app-injected icon-only buttons ------------------------------ */
  /* Third-party apps (Globo filter collection cards) render icon-only
     add-to-cart buttons with no accessible name
     (.spf-product__form-btn-addtocart). Name them, including the product
     title when findable so each card's button is distinct (2.4.6 / 4.1.2). */
  function nameIconButtons() {
    var btns = document.querySelectorAll('button, input[type=submit]');
    for (var i = 0; i < btns.length; i++) {
      var el = btns[i];
      if (hasName(el)) { continue; }
      if ((el.textContent || '').trim()) { continue; } /* has visible text */
      var isAdd = /addtocart|add[_\-]?to[_\-]?cart/i.test('' + el.className) ||
        (el.form && /\/cart\/add/.test(el.form.getAttribute('action') || ''));
      if (!isAdd) { continue; }
      var card = el.parentElement && el.parentElement.closest(
        '.spf-product-card, .product-item, li, article'
      );
      var t = card && card.querySelector('[class*="title"] a, a[class*="title"], [class*="title"]');
      var title = t && (t.textContent || '').trim();
      el.setAttribute('aria-label', title ? 'Add to cart: ' + title.slice(0, 80) : 'Add to cart');
    }
  }

  /* ---- 2b2. identify input purpose (autocomplete) ----------------------- */
  /* WCAG 1.3.5 (AA): inputs collecting the user's own info must declare their
     purpose via autocomplete. The theme's Vue form fields ship none; map by
     name/type at runtime (setting the attribute on the rendered input sticks). */
  var AUTOCOMPLETE_MAP = [
    [/first[_\- ]?name|fname|given/i, 'given-name'],
    [/last[_\- ]?name|lname|family|surname/i, 'family-name'],
    [/company|organization/i, 'organization'],
    [/address.?2|apt|suite|unit|line.?2/i, 'address-line2'],
    [/address|street|line.?1/i, 'address-line1'],
    [/city|town/i, 'address-level2'],
    [/province|state|region/i, 'address-level1'],
    [/zip|postal/i, 'postal-code'],
    [/country/i, 'country-name'],
    [/phone|tel|mobile/i, 'tel'],
    [/email|e[_\- ]?mail/i, 'email']
  ];
  function setAutocomplete() {
    var inputs = document.querySelectorAll(
      'input:not([type=hidden]):not([type=submit]):not([type=button]):not([type=search]):not([autocomplete])'
    );
    for (var i = 0; i < inputs.length; i++) {
      var el = inputs[i];
      if (el.type === 'email') { el.setAttribute('autocomplete', 'email'); continue; }
      if (el.type === 'tel') { el.setAttribute('autocomplete', 'tel'); continue; }
      if (el.type === 'password') {
        el.setAttribute('autocomplete', /login|sign-?in/i.test(location.pathname) ? 'current-password' : 'new-password');
        continue;
      }
      var key = (el.getAttribute('name') || '') + ' ' + (el.id || '');
      for (var j = 0; j < AUTOCOMPLETE_MAP.length; j++) {
        if (AUTOCOMPLETE_MAP[j][0].test(key)) { el.setAttribute('autocomplete', AUTOCOMPLETE_MAP[j][1]); break; }
      }
    }
  }

  /* ---- 2c. name image-only links/buttons -------------------------------- */
  /* Card links (article/product/collection/tile) often wrap only a lazy-loaded
     image. If the link has no text, derive its name from the image alt, else
     from the card's heading/title. Safety net covering every card variant
     across all templates. (WCAG 2.4.4, 4.1.2) */
  function linkHasName(el) {
    if ((el.getAttribute('aria-label') || '').trim()) { return true; }   // non-EMPTY aria-label only
    if (el.getAttribute('aria-labelledby')) { return true; }
    // innerText (not textContent) so a <noscript> fallback's raw markup — a
    // non-rendered text node when JS is on — doesn't count as visible text.
    var vt = (el.innerText !== undefined ? el.innerText : el.textContent) || '';
    if (vt.trim()) { return true; }
    if ((el.getAttribute('title') || '').trim()) { return true; }
    var img = el.querySelector('img[alt]');
    if (img && (img.getAttribute('alt') || '').trim()) { return true; }
    return false;
  }
  function nameImageLinks() {
    var els = document.querySelectorAll('a, button');
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (linkHasName(el)) { continue; }
      // Derive a name from the card's title (curated containers — no greedy
      // [class*=item] which would match an inner image wrapper).
      var name = '';
      var card = el.closest('.product-item, .article-item, .article-block, .tile, .collection-element, .dropdown-block, .cart-item, article, li');
      if (card) {
        var t = card.querySelector('[class*="__title"]') ||
                card.querySelector('h1, h2, h3, h4') ||
                card.querySelector('[class*="title"]');
        if (t) { name = (t.textContent || '').trim().slice(0, 120); }
      }
      if (name) {
        el.setAttribute('aria-label', name);
        continue;
      }
      // Still unnameable. If it's an empty placeholder link (skeleton card with
      // no destination and no content), remove it from the a11y tree / tab order
      // so it isn't announced as a nameless link. Only for truly empty hrefs —
      // never "#" (which is often a real JS toggle, and those are labeled in markup).
      if (el.tagName === 'A') {
        var href = el.getAttribute('href');
        if (href === null || href === '') {
          el.setAttribute('aria-hidden', 'true');
          el.setAttribute('tabindex', '-1');
          continue;
        }
        // Last resorts for image-only links with no derivable name (2.4.4):
        // 1. Internal product links (related-product thumbnails in description
        //    tabs): humanize the URL handle — "/products/ac990-steel-drain"
        //    announces as "Ac990 steel drain". More reliable than per-image
        //    alt across thousands of merchant-entered thumbnails.
        // 2. External links (e.g. a partner logo on a content page): announce
        //    the destination host. Internal non-product links are left for
        //    real alt text — a same-site hostname label would be meaningless.
        if (el.querySelector('img') && /^https?:/i.test(el.href || '')) {
          try {
            var u = new URL(el.href);
            var m = u.pathname.match(/^\/products\/([a-z0-9-]+)/i);
            if (m) {
              var words = m[1].replace(/-/g, ' ').trim();
              el.setAttribute('aria-label', words.charAt(0).toUpperCase() + words.slice(1));
            } else if (u.hostname !== window.location.hostname) {
              el.setAttribute('aria-label', u.hostname.replace(/^www\./, ''));
            }
          } catch (e) { /* malformed href — leave as-is */ }
        }
      }
    }
  }

  /* ---- 2c2. duplicate image alts inside one link --------------------------- */
  /* Product cards ship a primary + hover-swap image, both with the product
     title as alt — the link's accessible name becomes "Title Title" (VO sweep
     finding). Keep the first occurrence, blank exact duplicates. (2.4.4) */
  function dedupeLinkImageAlts() {
    var links = document.querySelectorAll('a[href]');
    for (var i = 0; i < links.length; i++) {
      var imgs = links[i].querySelectorAll('img[alt]');
      if (imgs.length < 2) { continue; }
      var seen = {};
      for (var k = 0; k < imgs.length; k++) {
        var alt = (imgs[k].getAttribute('alt') || '').trim().toLowerCase();
        if (!alt) { continue; }
        if (seen[alt]) { imgs[k].setAttribute('alt', ''); }
        else { seen[alt] = true; }
      }
    }
  }

  /* ---- 2d. safety net: every <img> must have an alt attribute ------------ */
  /* Editorial/merchant rich-text (blog bodies, product description tabs) often
     contains images with no alt at all. A missing alt is a hard failure; an
     empty alt is valid (decorative). This guarantees no <img> ships without an
     alt. NOTE: meaningful content images should still get REAL alt text entered
     in the admin — this net only prevents the hard violation. (WCAG 1.1.1) */
  function ensureImageAlt() {
    var imgs = document.querySelectorAll('img:not([alt])');
    for (var i = 0; i < imgs.length; i++) {
      imgs[i].setAttribute('alt', '');
    }
  }

  /* ---- 2e. certification/award marks in product descriptions ------------- */
  /* A full-catalog scan (959 products, 2026-06-11) found the same ~10
     certification/award logos embedded alt-less in 376 product descriptions
     (IAPMO x288, WaterSense x47, Intertek x31, ...). They convey real
     information (the product is certified), so blanket alt="" loses meaning.
     A closed filename->alt map fixes every placement at once; admin-entered
     alt still wins because non-empty alt is never overwritten. (WCAG 1.1.1) */
  var CERT_ALT = [
    [/iapmo/i, 'IAPMO certified'],
    [/watersense/i, 'EPA WaterSense labeled'],
    [/intertek/i, 'ETL Listed mark - certified by Intertek'],
    [/good-design-award/i, 'Good Design Award'],
    [/hospitality-design/i, 'Hospitality Design Award'],
    [/california_energy|cec_california/i, 'California Energy Commission certified'],
    [/nyc-x-design/i, 'NYCxDESIGN Award'],
    [/dwell-on-design/i, 'Dwell on Design award'],
    [/westedge/i, 'WestEdge Design Fair'],
    [/designlines/i, 'Designlines Award']
  ];
  function altForCertMarks() {
    var imgs = document.querySelectorAll('img[src]');
    for (var i = 0; i < imgs.length; i++) {
      var el = imgs[i];
      var alt = el.getAttribute('alt');
      if (alt && alt.trim()) { continue; }
      for (var k = 0; k < CERT_ALT.length; k++) {
        if (CERT_ALT[k][0].test(el.src)) {
          el.setAttribute('alt', CERT_ALT[k][1]);
          break;
        }
      }
    }
  }

  /* ---- 2e2. approved content alt map -------------------------------------- */
  /* The reviewed-and-approved alt texts for merchant content images (hero
     slide brand logos, membership marks, blog photography) keyed by image
     filename — fortebrands-alt-text-review.txt is the source of truth.
     Applied only when the image has no admin-entered alt (which always wins).
     If an image file is replaced in the admin, set real alt there. */
  var CONTENT_ALT = [
    [/CRW-LDN_logo_RGB-Rev/i, 'Crosswater London'],
    [/ressa-x3-logo_f088a522-14b8-4e5f-b25d-84fb316f4b14/i, 'Ressa X3 Spa Toilet'],
    [/landmade-logo-fb-01/i, 'Landmade'],
    [/MGS-Milan-Logo-EN-Rev/i, 'MGS Milan'],
    [/DD_FINAL_MELB_LOGO_2022_WHITE/i, 'Designer Doorware'],
    [/kwc-logo_6e1cf698-22d5-4d19-a58c-c559f2e0d499/i, 'KWC'],
    [/franzlogo-new/i, 'Franz Viegener'],
    [/OCEANIA_Logo_ENG_CMYK_REV_reg/i, 'Oceania'],
    [/chambord-logo-2022/i, 'Chambord France'],
    [/CRW-LDN_logo_RGB/i, 'Crosswater London'],
    [/DD_FINAL_MELB_LOGO_2022_BLK/i, 'Designer Doorware'],
    [/franzlogo\.png/i, 'Franz Viegener'],
    [/landmade-logo/i, 'Landmade'],
    [/MGS-Milan-Logo-EN/i, 'MGS Milan'],
    [/OCEANIA-logo-grey/i, 'Oceania'],
    [/crosswater-logo-2023-white/i, 'Crosswater London'],
    [/mgs-logo\.png/i, 'MGS Milan'],
    [/OCEANIA_Logo_ENG_CMYK_reg_new/i, 'Oceania'],
    [/1_-logo\.svg/i, '1% for the Planet member'],
    [/franz-logo\.svg/i, 'Franz Viegener'],
    [/lindsaysalazarcom-49_w800_h800/i, 'Primary bathroom with powder-blue cabinetry, a mirrored slipper tub with Crosswater London tub filler at the window, crystal chandelier and tufted ottoman'],
    [/lindsaysalazarcom-37_w800_h800/i, 'Mirrored slipper tub with polished nickel tub filler and handshower by the window; rose velvet pajamas draped over a tufted bench'],
    [/lindsaysalazarcom-23_w800_h800/i, 'Powder-blue vanity with marble top and widespread faucet beside a brass etagere stacked with towels and toiletries'],
    [/lindsaysalazarcom-17_w800_h800/i, 'Marble walk-in shower with glass door and a brass cart of towels'],
    [/lindsaysalazarcom-1_w800_h800/i, 'View past the powder-blue vanity to a brass cart of white towels and hydrangeas; marble floor with taupe diamond inlay'],
    [/lindsaysalazarcom-20_w800_h800/i, 'Brass shelf cart with flowers and toiletries beside the marble shower'],
    [/lindsaysalazarcom-47_w800_h800/i, 'Upholstered ottoman under a crystal chandelier in the bathroom sitting area'],
    [/lindsaysalazarcom-51_w800_h800/i, 'Woman in a pink robe standing beside the freestanding tub at the window'],
    [/lindsaysalazarcom-39_w800_h800/i, 'Standing at the mirrored slipper tub, adjusting the floor-mounted polished nickel tub filler with porcelain-handled handshower'],
    [/lindsaysalazarcom-26_w800_h800/i, 'Traditional floor-mounted tub filler in polished nickel with cross handles, porcelain-grip handshower and coiled hose'],
    [/lindsaysalazarcom-6_w800_h800/i, 'Washing hands at the marble sink with a polished nickel widespread faucet'],
    [/lindsaysalazarcom-32_w800_h800/i, 'Close-up of polished nickel cross-handle faucet at the marble basin'],
    [/lindsaysalazarcom-13_w800_h800/i, 'Adjusting the polished nickel shower valve trim on the marble shower wall'],
    [/lindsaysalazarcom-27_w800_h800/i, 'Marble shower with ceiling-mount rain shower head'],
    [/lindsaysalazarcom-3_w800_h800/i, 'Powder room with ornate blue-and-coral medallion wallpaper panels, white toilet and marble floor with taupe diamond inlay'],
    [/lindsaysalazarcom-5_w800_h800/i, 'White basin with cross-handle widespread faucet and styled toiletries'],
    [/lindsaysalazarcom-15_w800_h800/i, 'Pale blue makeup vanity with round mirror and pink stool'],
    [/lindsaysalazarcom-25_w800_h800/i, 'Makeup vanity with ornate gold mirror and pink upholstered seat'],
    [/lindsaysalazarcom-43_w800_h800/i, 'Primary bathroom furnishings: tufted ottoman on an antique rose rug, mirrored slipper tub, powder-blue cabinetry and a makeup vanity with pink bench'],
    [/waldorf-set-a_w800_h800/i, 'Charcoal bathroom with black wall-hung vanity and white basin, hexagonal backlit mirror, chrome towel ladder, wall-hung toilet and candelabra floor lamp'],
    [/pro1000dnm-a_w800_h800/i, 'Matte black MPRO dual-control concealed shower valve on a marble wall'],
    [/pro120wnm-b_w800_h800/i, 'Matte black wall-mounted basin spout running water'],
    [/ub422db-c_w800_h800/i, 'Matte black freestanding tub filler beside a white tub against a brick wall'],
    [/set-02-berea-black-hires_w800_h800/i, 'Traditional bathroom with black-exterior Balthazar freestanding tub, floor-mounted tub filler, corner shower, pedestal basin and beaded chandelier'],
    [/2-tiles-700x700/i, 'Three black-tile bathroom ideas: black penny-round wall tiles with brass pendants, white tub surround on a black hexagon floor, and a white room grounded by black hex tiles'],
    [/3-walls-700x700/i, 'Two dark bathrooms: navy walls with art, pink textiles on a brass rail and starburst floor tiles; and a black wall with round mirror, wood vanity, concrete basin and wall-mounted faucet'],
    [/4-accessories-700x700/i, 'White hexagon-tiled bathroom with black ladder towel rack and black-framed mirror'],
    [/CWL-Ressa-Penta-Hero-1920x720/i, 'Ressa spa toilet with wall-mounted remote against a white brick wall, beside a rattan-front cabinet and potted snake plant'],
    [/ressa-x2\.png/i, 'Wall-hung Ressa spa toilet with remote beside a floating fluted vanity, twin round mirrors and a wood slat bench'],
    [/lobby\.jpg/i, 'Multi-storey hotel atrium lined with curved timber slats'],
    [/lobby2_w800_h800/i, 'Hotel lounge with grand piano under a rippling timber ceiling'],
    [/lobby3\.jpg/i, 'Hotel atrium skylight ringed by curved balconies'],
    [/room3_w800_h800/i, 'Hotel guest room with patterned headboard wall and floor-to-ceiling windows'],
    [/room4_w800_h800/i, 'Hotel guest room in beige and gold tones with a window seat'],
    [/dsc-2499_w800_h800/i, 'Polished nickel wall-mount tub faucet with handshower on a marble surround'],
    [/dsc-2570_w800_h800/i, 'Wall-mounted chrome bath filler with twin knobs running water into the tub, set in a marble surround'],
    [/dsc-2632_w800_h800/i, 'Rain shower head running water'],
    [/different-finishes-3/i, 'Close-up of the UNION knurled lever handle in Brushed Black Chrome'],
    [/different-finishes-2-alt/i, 'Freestanding white tub in a loft with city-skyline windows'],
    [/different-finishes-4/i, 'White basin on a hexagon-tile wall with widespread faucet and blue flowers'],
    [/different-finishes-5/i, 'Polished nickel cross-handle basin faucet on white marble'],
    [/image-1_w800_h800/i, 'Unlacquered brass widespread basin faucet on a dark counter'],
    [/image-2_w800_h800/i, 'Gloved hands polishing a brass faucet body on a buffing wheel'],
    [/image-dave-h_w800_h800/i, 'Dave Hance polishing a chrome UNION faucet with a cloth'],
    [/image-3_w800_h800/i, 'Brass faucet body clamped in a precision measuring machine with digital readout'],
    [/image-wheel_w800_h800/i, 'Two close-ups of the UNION Wheel control with knurled edging in a black finish'],
    [/image-red_w800_h800/i, 'UNION Red Lever in chrome: basin faucet beside a matte black vessel basin, and an exposed shower set with round rain head on a concrete wall'],
    [/image-4_w800_h800/i, 'UNION in brushed nickel: single-hole basin faucet running into a white basin against whitewashed brick, and a freestanding bath filler with handshower beside the tub'],
    [/image-5_w800_h800/i, 'UNION bath filler with Wheel handles and handshower in Brushed Black Chrome at the tub edge; matching wall-mounted basin faucet behind'],
    [/image-6_w800_h800/i, 'UNION in polished gold: freestanding bath filler running into the tub against navy panelling, and a wall-mounted lever faucet over a white vessel basin'],
    [/grassybay-ad-25-web_w800_h800/i, 'Black bateau tub beside a glass steam shower where a picture window frames the lake; white subway tile and river-rock pebble floor'],
    [/grassybay-ad-23-web_w800_h800/i, 'Cerused-wood double vanity with black stone vessel basins, wall-mounted black faucets and globe-lit framed mirrors on a pebble floor'],
    [/grassybay-ad-22-web_w800_h800/i, 'Gray subway-tiled attic shower with matte black valve and handshower, stone bench with towel, and a window to the lake'],
    [/grassybay-ad-24-web_w800_h800/i, 'Black bateau tub with black floor-mount filler beneath a black-and-white pier photograph; glass shower alongside, pebble floor'],
    [/nathan-kyle-astro-design/i, 'Nathan Kyle of Astro Design Centre'],
    [/intertek\.png/i, 'ETL Listed mark — certified by Intertek']
  ];
  function applyContentAltMap() {
    var imgs = document.querySelectorAll('img[src]');
    for (var i = 0; i < imgs.length; i++) {
      var el = imgs[i];
      var alt = el.getAttribute('alt');
      if (alt && alt.trim()) { continue; }
      for (var k = 0; k < CONTENT_ALT.length; k++) {
        if (CONTENT_ALT[k][0].test(el.src)) {
          el.setAttribute('alt', CONTENT_ALT[k][1]);
          break;
        }
      }
    }
  }

  /* ---- 2f. ensure a top-level heading exists ----------------------------- */
  /* Some merchant-built templates (home, brand/content pages) have no <h1> —
     their hero uses a styled <h2>. Screen-reader users navigate by headings, so
     a missing top-level heading hurts orientation. If a page has no <h1>, inject
     a visually-hidden one from the page title at the top of <main>. Only runs
     when zero <h1> exist, so it never creates a duplicate. (WCAG 2.4.6 — best practice) */
  function ensureH1() {
    if (document.querySelector('h1')) { return; }
    var target = document.getElementById('main') ||
      document.querySelector('main, [role="main"]') || document.body;
    if (!target) { return; }
    var title = (document.title || '').split('–')[0].split('|')[0].trim() || 'Forte Brands';
    var h1 = document.createElement('h1');
    h1.className = 'sr-only';
    h1.setAttribute('data-a11y-injected', '1');
    h1.textContent = title;
    target.insertBefore(h1, target.firstChild);
  }

  /* ---- 2e. announce cart changes to screen readers ---------------------- */
  /* Add-to-cart is AJAX (no page reload) and the theme has no live region, so a
     screen-reader user gets no feedback when an item is added — the exact gap in
     the complaint (cart notifications). A polite live region announces the new
     cart count. Polls the count text because the Vue node is replaced on update.
     (WCAG 4.1.3 Status Messages) */
  function setupCartAnnounce() {
    if (document.getElementById('a11y-cart-status')) { return; }
    var status = document.createElement('div');
    status.id = 'a11y-cart-status';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    status.setAttribute('aria-atomic', 'true');
    status.className = 'sr-only';
    document.body.appendChild(status);

    var last = null;
    function check() {
      var el = document.querySelector('.header-cart-icon__count');
      if (!el) { return; }
      var n = (el.textContent || '').trim();
      if (n === '') { return; }
      if (last === null) { last = n; return; }
      if (n !== last) {
        var added = parseInt(n, 10) > parseInt(last, 10);
        last = n;
        status.textContent = (added ? 'Item added to cart. ' : 'Cart updated. ') +
          n + (n === '1' ? ' item' : ' items') + ' in your cart.';
      }
    }
    setInterval(check, 800);
  }

  /* ---- 2g. expose & announce form validation errors --------------------- */
  /* The Vue forms show error text ("Email is required") but don't associate it
     with the field (no aria-invalid / aria-describedby) or announce it, so a
     screen-reader user gets no feedback on a failed submit. Associate each error
     to its input and announce a summary via an assertive live region.
     (WCAG 3.3.1 Error Identification, 4.1.3 Status Messages) */
  var _errId = 0;
  var _lastErrSig = '';
  function formStatusRegion() {
    var r = document.getElementById('a11y-form-status');
    if (!r) {
      r = document.createElement('div');
      r.id = 'a11y-form-status';
      r.setAttribute('role', 'alert');
      r.setAttribute('aria-live', 'assertive');
      r.className = 'sr-only';
      document.body.appendChild(r);
    }
    return r;
  }
  function enhanceFormErrors() {
    var errs = document.querySelectorAll('.form-row__error__text-error, .text-input__error');
    var msgs = [];
    for (var i = 0; i < errs.length; i++) {
      var p = errs[i];
      var txt = (p.textContent || '').trim();
      if (!txt || !p.getClientRects().length) { continue; }
      if (msgs.indexOf(txt) === -1) { msgs.push(txt); }
      // NB: don't include a [class*="form-row"] selector here — the error <p>
      // itself is .form-row__error__text-error and would self-match.
      var field = p.closest('.text-input, .text-input__wrapper');
      var input = field ? field.querySelector('input, select, textarea') : null;
      if (input) {
        if (!p.id) { p.id = 'a11y-err-' + (++_errId); }
        input.setAttribute('aria-invalid', 'true');
        var d = input.getAttribute('aria-describedby') || '';
        if ((' ' + d + ' ').indexOf(' ' + p.id + ' ') === -1) {
          input.setAttribute('aria-describedby', (d ? d + ' ' : '') + p.id);
        }
      }
    }
    var sig = msgs.join('|');
    if (sig && sig !== _lastErrSig) {
      _lastErrSig = sig;
      formStatusRegion().textContent =
        (msgs.length === 1 ? 'Form error: ' : 'Form has ' + msgs.length + ' errors: ') +
        msgs.slice(0, 8).join('. ') + '.';
    } else if (!sig) {
      _lastErrSig = '';
    }
  }

  /* ---- 3. aria-expanded sync for header toggles -------------------------- */
  function syncHeaderExpanded() {
    var header = document.getElementById('header');
    if (!header) { return; }
    var navOpen = header.classList.contains('is-header-nav-expanded');
    var searchOpen = header.classList.contains('is-header-search-expanded');
    var burgers = document.querySelectorAll('.btn-burger, .header__btn-burger');
    var searches = document.querySelectorAll('.btn-search, .header__btn-search, .js-btn-toggle[data-js-class="is-header-search-expanded"]');
    var j;
    for (j = 0; j < burgers.length; j++) {
      burgers[j].setAttribute('aria-expanded', navOpen ? 'true' : 'false');
    }
    for (j = 0; j < searches.length; j++) {
      searches[j].setAttribute('aria-expanded', searchOpen ? 'true' : 'false');
    }
  }

  /* ---- init -------------------------------------------------------------- */
  /* ---- 2g. dismissible hover menus (WCAG 1.4.13) -------------------------- */
  /* The desktop mega-menu opens on pure CSS :hover and obscures page content,
     with no way to dismiss it without moving the pointer. Escape sets
     data-a11y-dismissed on the open item (accessibility.css hides its
     dropdown); the attribute clears on the next mouseenter so the menu
     behaves normally on the next deliberate hover. */
  function setupHoverDismiss() {
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape' && e.key !== 'Esc') { return; }
      var items = document.querySelectorAll('.nav__item--has-dropdown');
      for (var i = 0; i < items.length; i++) {
        var li = items[i];
        var dd = li.querySelector('.nav__dropdown');
        if (dd && dd.getClientRects().length) {
          li.setAttribute('data-a11y-dismissed', '');
          li.classList.remove('is-active');
          li.addEventListener('mouseenter', function onEnter(ev) {
            ev.currentTarget.removeAttribute('data-a11y-dismissed');
            ev.currentTarget.removeEventListener('mouseenter', onEnter);
          });
        }
      }
    });
  }

  /* ---- 2f2. settle lazy-loaded images early -------------------------------- */
  /* The theme's scroll-driven lazy loader swaps src/classes as the user moves
     through the page. Every swap mutates the accessibility tree, which resets
     the VoiceOver cursor — on image-heavy blog articles the cursor was yanked
     back to the breadcrumb on every reveal (body unreadable; VO sweep
     finding). Settle all images once, shortly after load: set src up front
     with native loading="lazy", so the BROWSER still defers offscreen fetches
     but the DOM never mutates mid-read. The theme's own loader sees the image
     as revealed (is-loaded) and leaves it alone. */
  function settleLazyImages() {
    var imgs = document.querySelectorAll('img.img__el[data-normal]:not([data-a11y-settled])');
    for (var i = 0; i < imgs.length; i++) {
      var el = imgs[i];
      el.setAttribute('data-a11y-settled', '1');
      if (!el.getAttribute('src')) {
        el.setAttribute('loading', 'lazy');
        var retina = el.getAttribute('data-retina');
        el.setAttribute('src', (window.devicePixelRatio > 1 && retina) ? retina : el.getAttribute('data-normal'));
        var srcset = el.getAttribute('data-srcset');
        if (srcset) { el.setAttribute('srcset', srcset); }
      }
      var pic = el.closest('picture');
      if (pic && !pic.classList.contains('is-loaded')) { pic.classList.add('is-loaded'); }
    }
  }
  function scheduleImageSettle() {
    var run = function () { setTimeout(settleLazyImages, 1200); };
    if (document.readyState === 'complete') { run(); }
    else { window.addEventListener('load', run); }
    /* late-rendered content (app widgets, Vue) settles via the observer */
  }

  /* ---- 2g2. bare inline SVGs announced as "image" ------------------------- */
  /* VoiceOver announces an unlabeled <svg> as just "image" (206 instances in
     the site-wide VO transcript sweep, mostly app-injected icons). An SVG with
     no accessible name conveys nothing — mark it decorative. Never touches
     SVGs that carry a name (aria-label/labelledby, role=img, or a <title>). */
  function hideBareSvgs() {
    var svgs = document.querySelectorAll(
      'svg:not([aria-hidden]):not([aria-label]):not([aria-labelledby]):not([role])'
    );
    for (var i = 0; i < svgs.length; i++) {
      var el = svgs[i];
      if (el.querySelector('title')) { continue; }
      el.setAttribute('aria-hidden', 'true');
      el.setAttribute('focusable', 'false');
    }
  }

  /* ---- 2g3. Globo filter search controls ---------------------------------- */
  /* The filter app renders an unnamed icon-only clear button and a clickable
     <div> as the search submit (unnamed AND keyboard-inaccessible). Found by
     the VO sweep on every collection page. (WCAG 2.1.1, 4.1.2) */
  function fixGloboSearchControls() {
    /* The app duplicates id="gf-controls-search-label"/-input (desktop+mobile
       trees), making the second pair's label association ambiguous (vendor
       ticket filed). An explicit aria-label outranks the label[for] in the
       accessible-name computation, so both inputs announce correctly. */
    var gfInputs = document.querySelectorAll('.gf-controls-search-input:not([aria-label])');
    for (var g = 0; g < gfInputs.length; g++) {
      gfInputs[g].setAttribute('aria-label', 'Search products in this collection');
    }
    var clears = document.querySelectorAll('.gf-controls-clear-button:not([aria-label])');
    for (var i = 0; i < clears.length; i++) {
      clears[i].setAttribute('aria-label', 'Clear search');
    }
    var subs = document.querySelectorAll('.gf-controls-search-button:not([data-a11y-key])');
    for (var k = 0; k < subs.length; k++) {
      var el = subs[k];
      if (el.tagName !== 'BUTTON' && el.tagName !== 'A') {
        el.setAttribute('role', 'button');
        if (!el.hasAttribute('tabindex')) { el.setAttribute('tabindex', '0'); }
        el.addEventListener('keydown', activate);
      }
      if (!el.getAttribute('aria-label')) { el.setAttribute('aria-label', 'Search products'); }
      el.setAttribute('data-a11y-key', '1');
    }
  }

  /* ---- 2g4. Shopify Inbox chat toggle ------------------------------------- */
  /* The chat app's toggle button ships icon-only with no accessible name
     (axe button-name, critical, every page). (WCAG 4.1.2) */
  function nameChatToggle() {
    var els = document.querySelectorAll('button.chat-toggle:not([aria-label])');
    for (var i = 0; i < els.length; i++) { els[i].setAttribute('aria-label', 'Chat'); }
  }

  /* ---- 2h. cart drawer hidden from AT while closed ------------------------ */
  /* The slide-out cart drawer (.header-cart, Vue ':class={active}') is only
     hidden visually by off-screen positioning when closed — VoiceOver's first
     heading on any page was the drawer's "Your Cart" h3. Sync aria-hidden to
     the open state; the inert-sync (section 1) then also removes its controls
     from the tab order. (WCAG 1.3.2, 2.4.3, 4.1.2) */
  function syncCartDrawer() {
    var drawers = document.querySelectorAll('.header-cart');
    for (var i = 0; i < drawers.length; i++) {
      var d = drawers[i];
      var open = d.classList.contains('active');
      if (!open && d.getAttribute('aria-hidden') !== 'true') {
        d.setAttribute('aria-hidden', 'true');
      } else if (open && d.getAttribute('aria-hidden') === 'true') {
        d.removeAttribute('aria-hidden');
      }
    }
  }

  function init() {
    syncAll(document);
    freeNavThumbnails();
    /* The carousel-visibility exception in sync() needs FINAL layout — at
       init, cells can still measure 0x0 and get wrongly inerted (found live:
       the second visible gallery image was dead). Re-reconcile after load and
       on resize, when rects are real. */
    var resync = function () { setTimeout(function () { syncAll(document); freeNavThumbnails(); }, 1200); };
    if (document.readyState === 'complete') { resync(); }
    else { window.addEventListener('load', resync); }
    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () { syncAll(document); freeNavThumbnails(); }, 400);
    });
    ensureH1();
    ensureImageAlt();
    altForCertMarks();
    applyContentAltMap();
    upgradeFauxButtons();
    nameFormControls();
    nameIconButtons();
    setAutocomplete();
    nameImageLinks();
    enhanceFormErrors();
    setupCartAnnounce();
    syncHeaderExpanded();
    setupHoverDismiss();
    syncCartDrawer();
    hideBareSvgs();
    fixGloboSearchControls();
    dedupeLinkImageAlts();
    nameChatToggle();
    scheduleImageSettle();

    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var m = mutations[i];
        if (m.type === 'attributes') {
          if (m.attributeName === 'aria-hidden' || m.attributeName === 'inert') {
            if (m.target.closest && m.target.closest('.product-hero__images-dots')) { freeNavThumbnails(); }
            else if (m.attributeName === 'aria-hidden') { sync(m.target); }
          }
          else if (m.attributeName === 'class' && m.target.id === 'header') {
            syncHeaderExpanded();
          }
          else if (m.attributeName === 'class' && m.target.classList &&
                   m.target.classList.contains('header-cart')) {
            syncCartDrawer();
          }
        } else if (m.type === 'childList' && m.addedNodes.length) {
          for (var k = 0; k < m.addedNodes.length; k++) {
            var node = m.addedNodes[k];
            if (node.nodeType !== 1) { continue; }
            if (node.getAttribute && node.getAttribute('aria-hidden') === 'true') { sync(node); }
            if (node.querySelectorAll) { syncAll(node); }
          }
          ensureImageAlt();
          altForCertMarks();
          applyContentAltMap();
          hideBareSvgs();
          fixGloboSearchControls();
          dedupeLinkImageAlts();
          nameChatToggle();
          ensureH1();
          if (document.readyState === 'complete') { settleLazyImages(); }
          upgradeFauxButtons();
          nameFormControls();
          nameIconButtons();
          setAutocomplete();
          nameImageLinks();
          enhanceFormErrors();
        }
      }
    });

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['aria-hidden', 'class', 'inert'],
      subtree: true,
      childList: true
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
